import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { useShift } from '@/hooks/useShift';
import {
  cancelTaskAlarmNotifications,
  ensureTaskAlarmChannel,
  scheduleTaskAlarmNotifications,
} from '@/lib/task-alarm-native';

/**
 * Schedules task alarms for the caregiver's active shift at app level, so the
 * alarms fire even when the app is closed / in the background and regardless of
 * which screen the caregiver last visited.
 */
export function TaskAlarmScheduler() {
  const { t } = useTranslation();
  const { activeShift } = useShift();
  const shiftId = activeShift?.id ?? null;
  const templateId = activeShift?.shift_template_id ?? null;
  const patientId = activeShift?.patient_id ?? null;
  const runningRef = useRef(false);

  const reschedule = useCallback(async () => {
    if (!Capacitor.isNativePlatform() || runningRef.current) return;
    runningRef.current = true;
    try {
      if (!shiftId || !templateId) {
        await cancelTaskAlarmNotifications();
        return;
      }

      const [{ data: template }, { data: instances }] = await Promise.all([
        supabase
          .from('shift_templates')
          .select('alarm_enabled, alarm_repeat_minutes, alarm_max_repeats')
          .eq('id', templateId)
          .maybeSingle(),
        supabase
          .from('shift_task_instances')
          .select('id, scheduled_for, status, task_template:care_plan_tasks(name)')
          .eq('shift_id', shiftId)
          .order('scheduled_for', { ascending: true }),
      ]);

      if (!template?.alarm_enabled) {
        await cancelTaskAlarmNotifications();
        return;
      }

      const tasks = (instances || []).map((row: any) => ({
        id: row.id as string,
        scheduled_for: row.scheduled_for as string,
        isCompleted: row.status === 'completed' || row.status === 'skipped',
        name: row.task_template?.name || t('taskAlarm.genericTask'),
        patientId,
      }));

      await scheduleTaskAlarmNotifications(tasks, template, {
        body: t('taskAlarm.nativeBody'),
        logLabel: `shift=${shiftId}`,
      });
    } catch (e) {
      console.warn('[task-alarms] scheduler failed', e);
    } finally {
      runningRef.current = false;
    }
  }, [shiftId, templateId, patientId, t]);

  // Create the channel once on native.
  useEffect(() => {
    void ensureTaskAlarmChannel();
  }, []);

  // Reschedule when the active shift changes (check-in / check-out).
  useEffect(() => {
    void reschedule();
  }, [reschedule]);

  // Reschedule when the app returns to the foreground.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let remove: (() => void) | undefined;
    (async () => {
      const handle = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void reschedule();
      });
      remove = () => handle.remove();
    })();
    return () => remove?.();
  }, [reschedule]);

  // Reschedule when tasks of the active shift change (completed, added, removed).
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !shiftId) return;
    const channel = supabase
      .channel(`task-alarms-${shiftId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shift_task_instances', filter: `shift_id=eq.${shiftId}` },
        () => void reschedule(),
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [shiftId, reschedule]);

  return null;
}
